import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import UpgradePage from "pages/dashboard/UpgradePage";
import ApprovalPage from "pages/dashboard/global-admin/ApprovalPage";
import AssistantManagerPage from "pages/dashboard/global-admin/AssistantManagerPage";
import UsageMonitorPage from "pages/dashboard/global-admin/UsageMonitorPage";
import PromptsManagerPage from "pages/dashboard/admin/prompts/PromptsManagerPage";
import DashboardLayout from "components/dashboard/DashboardLayout";
import Settings from "components/dashboard/Settings";
import ProfessionalOnboardingDialog from "components/dashboard/ProfessionalOnboardingDialog";
import HealthRecords from "components/dashboard/HealthRecords";
import { db } from "services/firebase";
import { PromptsManagerProvider } from "context/PromptsManager";
import { ChartMindSessionProvider } from "context/ChartMindSession";
import PeerViewPage from "pages/dashboard/professional/PeerViewPage";
import ChartMindPage from "pages/dashboard/professional/ChartMindPage";
import MyChartsPage from "pages/dashboard/professional/MyChartsPage";
// [BETA-KIJABE] Import for Kijabe beta route — remove when beta period ends
import AskMeAnythingPage from "pages/AskMeAnythingPage";
import DebugInstallPage from "pages/dashboard/DebugInstallPage";
import HomePage from "pages/dashboard/HomePage";

const ProfessionalRoutes = () => {
  const { user, userData, subscription, isGlobalAdmin } = useAuth();
  const location = useLocation();
  const [showProfessionalOnboarding, setShowProfessionalOnboarding] =
    useState(false);

  // [BETA-KIJABE] Temporary flag for Kijabe beta testers — remove when beta period ends
  const isBetaKijabe = Boolean(userData?.betaAccess);

  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (user?.uid) {
        try {
          const userDoc = await db.collection("users").doc(user.uid).get();
          const userData = userDoc.data();

          if (
            userData?.role === "professional" &&
            !userData?.hideOnboardingDialog
          ) {
            setShowProfessionalOnboarding(true);
          }
        } catch (error) {
          console.error("Error checking onboarding status:", error);
        }
      }
    };

    checkOnboardingStatus();
  }, [user]);

  // Full-width for chat surfaces and ChartMind editor (not the session list)
  const isChatRoute = () => {
    const path = location.pathname;
    const isChartMindSessionList = /\/chartmind\/sessions\/?$/.test(path);
    return (
      path === "/dashboard" ||
      path === "/dashboard/" ||
      path.includes("/peerview") ||
      // [BETA-KIJABE] Full-width for Kijabe AMA — remove when beta period ends
      path.includes("/ask-me-anything") ||
      (path.includes("/chartmind") && !isChartMindSessionList)
    );
  };

  return (
    <DashboardLayout fullWidth={isChatRoute()}>
      <ProfessionalOnboardingDialog
        open={showProfessionalOnboarding}
        onClose={() => setShowProfessionalOnboarding(false)}
        userId={user?.uid}
        userData={userData}
      />

      <PromptsManagerProvider>
        {/*
          ChartMindSessionProvider mounts above <Routes> so the in-progress encounter
          survives navigation (e.g. user goes to ChatPage and comes back). State is RAM-only.
        */}
        <ChartMindSessionProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/peerview" element={<PeerViewPage />} />
          {/* [BETA-KIJABE] Kijabe-only AMA route — remove when beta period ends */}
          <Route
            path="/ask-me-anything"
            element={
              isBetaKijabe
                ? <AskMeAnythingPage isDashboard />
                : <Navigate to="/dashboard" replace />
            }
          />
          {/* Session lives in ChartMindSessionProvider above — no key= prop, no disableSave prop. */}
          <Route path="/chartmind" element={<ChartMindPage />} />
          {/* [BETA-KIJABE] Beta users can't access session history — redirect to base — remove when beta ends */}
          <Route
            path="/chartmind/sessions"
            element={isBetaKijabe ? <Navigate to="/dashboard/chartmind" replace /> : <MyChartsPage />}
          />
          {/* [BETA-KIJABE] Beta users can't load saved sessions — redirect to base — remove when beta ends */}
          <Route
            path="/chartmind/:sessionId"
            element={
              isBetaKijabe
                ? <Navigate to="/dashboard/chartmind" replace />
                : <ChartMindPage />
            }
          />

          {/* ChartMind V2 (ClinicalHUD2) was folded into ChartMind; its old links land there. */}
          {/* MorningReport is off for professionals for now; learners have it
              (LearnerRoutes). */}
          {["/chartmind-v2/*", "/clinical-hud-2/*"].map((path) => (
            <Route key={path} path={path} element={<Navigate to="/dashboard/chartmind" replace />} />
          ))}

          <Route
            path="/settings"
            element={<Settings uid={user?.uid} subscription={subscription} />}
          />
          <Route path="/health-records" element={<HealthRecords />} />
          {/* Not linked from any nav — see DebugInstallPage.jsx. */}
          <Route path="/debug-install" element={<DebugInstallPage />} />

          {/* [BETA-KIJABE] Upgrade route hidden for Kijabe beta users — remove condition when beta ends */}
          {!isBetaKijabe && !userData?.dailyPassExpiresAt && (
            <Route path="/upgrade" element={<UpgradePage />} />
          )}

          {/* Global-admin-only routes */}
          <Route
            path="/admin/approval"
            element={
              isGlobalAdmin ? (
                <ApprovalPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/assistants"
            element={
              isGlobalAdmin ? (
                <AssistantManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/admin/usage"
            element={
              isGlobalAdmin ? (
                <UsageMonitorPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />
          <Route
            path="/prompts"
            element={
              isGlobalAdmin ? (
                <PromptsManagerPage />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            }
          />

          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
        </ChartMindSessionProvider>
      </PromptsManagerProvider>
    </DashboardLayout>
  );
};

export default ProfessionalRoutes;
